import { useState, useCallback, useEffect } from 'react';
import { Shield, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { UserRole, type ApplicationUser, type UpdateUserRoleRequest } from '@/types';

interface EditUserRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: ApplicationUser | null;
  onSubmit: (userId: string, data: UpdateUserRoleRequest) => void;
  isSubmitting?: boolean | undefined;
}

const ROLE_OPTIONS: { value: UserRole; label: string; description: string }[] = [
  {
    value: 'MigrationAdmin',
    label: 'Admin',
    description: 'Full access to all features including settings',
  },
  {
    value: 'MigrationOperator',
    label: 'Operator',
    description: 'Can start and manage migrations',
  },
  {
    value: 'MigrationViewer',
    label: 'Viewer',
    description: 'Read-only access to view migrations',
  },
];

export function EditUserRoleDialog({
  open,
  onOpenChange,
  user,
  onSubmit,
  isSubmitting,
}: EditUserRoleDialogProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole>(user?.role ?? 'MigrationViewer');
  const [reason, setReason] = useState('');

  // Reset when user changes
  useEffect(() => {
    if (user) {
      setSelectedRole(user.role);
      setReason('');
    }
  }, [user]);

  const handleSubmit = useCallback(() => {
    if (!user) return;

    onSubmit(user.id, {
      role: selectedRole,
      reason: reason.trim() || undefined,
    });
  }, [user, selectedRole, reason, onSubmit]);

  const handleClose = useCallback(() => {
    setReason('');
    onOpenChange(false);
  }, [onOpenChange]);

  const hasChanges = user && selectedRole !== user.role;

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Change User Role
          </DialogTitle>
          <DialogDescription>
            Update the role for {user.displayName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User Info */}
          <div className="p-3 border rounded-lg bg-muted/30">
            <p className="font-medium">{user.displayName}</p>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>

          {/* Current Role */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Current Role</span>
            <Badge variant="outline">
              {ROLE_OPTIONS.find((r) => r.value === user.role)?.label}
            </Badge>
          </div>

          {/* New Role Selection */}
          <div className="space-y-2">
            <Label>New Role</Label>
            <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as UserRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div>
                      <span className="font-medium">{option.label}</span>
                      <p className="text-xs text-muted-foreground">{option.description}</p>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason (optional) */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Change (Optional)</Label>
            <Textarea
              id="reason"
              placeholder="Enter a reason for this role change..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              This will be recorded in the audit log.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!hasChanges || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update Role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
